import { RoutingInfoService } from '@routing';
import { Component, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import {
    Router,
    ActivatedRoute,
    RouterOutlet,
    ActivationStart
} from '@angular/router';
import { ThemeService } from '@themes';

import { TenantService } from '@tenant';
import { AuthService } from '@auth';
import { GlobalEventBus, ActionEvents } from '@event-bus';
import { __i18n, LanguageService, TimezoneService } from '@localisation';
import { RoutingObserverService } from '@routing';
import { SiteModeService, SiteModes } from '@site-info';
import { LanguageDropDownService } from '@navigation';
import { InitWorkflowService } from '@workflow';
import { LoaderService, GlobalLoaderService } from '@loader';
import { Config } from '@config';
import { StateService, States } from '@state';
import { ResponsiveService } from '@responsive';
import { MessagesService, MessageType } from '@ui-messages';

// component translations
import { locale } from 'devextreme/localization';


//Do not remove dependencies which are marked as unused,
//they are included to create instances of them
@Component({
    selector: 'cl-app',
    templateUrl: 'app.template.html',
    standalone: false
})
export class AppComponent implements AfterViewInit {
    private routingInfos: any;

    public showLoader = true;
    public showAuthArea = false;

    public showMainArea = false;

    @ViewChild('landingPage', { static: true }) public landingPage: ElementRef;

    @ViewChildren(RouterOutlet)
    public outlet: QueryList<RouterOutlet>;

    private routeSubscriber: any;
    private lastRoute: any;

    public currentRouteName: string;

    public showResponsiveNavi = false;

    constructor(
        private router: Router,
        private theme: ThemeService,
        private tenantService: TenantService,
        private authService: AuthService,
        private changeDetector: ChangeDetectorRef,
        private routingObserver: RoutingObserverService,
        private routingInfo: RoutingInfoService,
        private messageBus: GlobalEventBus,
        private activatedRoute: ActivatedRoute,
        private siteMode: SiteModeService,
        public language: LanguageDropDownService,
        public workflow: InitWorkflowService,
        private loaderService: LoaderService,
        private config: Config,
        private stateService: StateService,
        private responsiveService: ResponsiveService,
        private globalLoaderService: GlobalLoaderService,
        private readonly languageService: LanguageService,
        private readonly messageService: MessagesService,
        private readonly timezoneServivce: TimezoneService
    ) {
        console.timeEnd('compile time');
       
        this.globalLoaderService.setText(__i18n(
            'loading tenant',
            'loader.text.loading.tenand',
            'text is shown beneath the loading indicator.'
        ));

        this.routingObserver.onRouteChanged.subscribe((current: any) => {
            this.currentRouteName = this.convertRouteToClassName(current.url);
        });

        theme.onLoad.subscribe((text: string) => {
            this.globalLoaderService.setText(text);
        });

        theme.onReady.subscribe(() => {
            this.showLoader = false;
            this.globalLoaderService.hide();
        });

        let routingStateSubscription: any;
        this.workflow.onReady.subscribe(() => {
            if(!this.isInitialised){
                this.messageBus.trigger(ActionEvents.applicationReady, { route: this.lastRoute });
                this.isInitialised = true;
            }
            if (routingStateSubscription) {
                routingStateSubscription.unsubscribe();
            }
            routingStateSubscription = this.checkRoutingState();
            changeDetector.detectChanges();
        });

        this.responsiveService.onChange.subscribe((size) => {
            this.handleResponsivSize(size.size);
        });
        this.handleResponsivSize(this.responsiveService.getSize().size);

        locale(this.languageService.language);
    }

    private isInitialised: boolean = false;

    private convertRouteToClassName(value: string) {
        value = value.startsWith('/') ? value.substr(1, value.length) : value;
        value = value.replace(/\//gm, '-');
        value = value.toLocaleLowerCase();
        return value;
    }

    private handleResponsivSize(size: string) {
        this.showResponsiveNavi = size === 'phone' || size === 'small-tablet';
        //this.changeDetector.detectChanges();
    }

    private checkRoutingState() {
        //if route changes, check if favourites should be displayed
        return this.routingObserver.onRouteChanged.subscribe((state: any) => {
            this.siteMode.changeMode(SiteModes.read);
        });
    }

    private lastOutletState: string = 'init';
    public ngAfterViewInit() {
        this.workflow.webInit();
        this.stateService.value(States.isLoggedOutOutlet).subscribe((value: any) => {
            if(this.lastOutletState !== value){
                const win = window as any;
                if (value) {
                    this.globalLoaderService.hide();
                    this.showAuthArea = true;
                    this.showMainArea = false;
                    document.getElementsByTagName('body')[0].classList.remove('application-outlet');
                    document.getElementsByTagName('body')[0].classList.add('landing-outlet');
                } else if(value === false) {
                    this.globalLoaderService.hide();
                    this.showAuthArea = false;
                    this.showMainArea = true;
                    document.getElementsByTagName('body')[0].classList.remove('landing-outlet');
                    document.getElementsByTagName('body')[0].classList.add('application-outlet');
                }else{
                    this.showAuthArea = false;
                    this.showMainArea = false;
                    this.globalLoaderService.show();
                }
            }
            this.lastOutletState = value;
            this.changeDetector.detectChanges();
        }, true);

        console.info('INITIAL_ERROR_MESSAGE: ' + this.config.serverConfig.INITIAL_ERROR_MESSAGE);
        if (this.config.serverConfig.INITIAL_ERROR_MESSAGE) {
            if (this.config.serverConfig.API_TOKEN) {
                this.messageService.addMessage({
                    text: this.config.serverConfig.INITIAL_ERROR_MESSAGE,
                    type: MessageType.error,
                    location: '/Home/StatusMap'
                });
            } else {
                this.messageService.addMessage({
                    text: this.config.serverConfig.INITIAL_ERROR_MESSAGE,
                    type: MessageType.error,
                    target: 'landingpage',
                    location: '/Login'
                });
            }
        }
    }

    private lastOutlet: any;
    public ngOnInit(){
        this.router.events.subscribe((e: any) => {
            //@Workaround for angular route activation bug
            if (e instanceof ActivationStart) {
                if(this.lastOutlet){
                    this.outlet.forEach((current: any) => {
                        current.deactivate();
                    });
                }
                this.lastOutlet = e.snapshot.outlet;
            }
        });
    }

    public logoClicked(e: any) {
        if (this.config.serverConfig.SIGNUP_EMAIL) {
            location.reload();
        }
    }
}
